import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment';

interface WindowWithGoogle extends Window {
  google?: { maps?: unknown };
}

const originalFetch = window.fetch.bind(window);
window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
  const headers =
    init?.headers instanceof Headers ? new Headers(init.headers) : new Headers(init?.headers ?? {});
  headers.delete('Authorization');

  const nextInit: RequestInit = {
    ...init,
    headers,
    credentials: 'include',
  };

  return originalFetch(input, nextInit);
};

// Lightweight loader to fetch Google Maps JS API at runtime.
function loadGoogleMapsJs(apiKey: string): Promise<void> {
  return new Promise(resolve => {
    // If no key provided, skip loading to avoid errors in environments without Maps usage.
    if (!apiKey) {
      console.warn('[Maps] No googleMapsApiKey provided; skipping Maps JS load');
      resolve();
      return;
    }

    // Already loaded
    const w = window as WindowWithGoogle;
    if (typeof w.google !== 'undefined' && w.google?.maps) {
      resolve();
      return;
    }

    // Avoid duplicate script tags
    const existing = document.getElementById('google-maps-js');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => resolve());
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-maps-js';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&v=weekly`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      console.error('[Maps] Failed to load Google Maps JS API');
      resolve();
    };
    document.head.appendChild(script);
  });
}

// Load Maps (if key present) before bootstrapping the app so consumers relying on
// window.google (e.g., geocoder) can work reliably.
(async () => {
  try {
    await loadGoogleMapsJs(environment.googleMapsApiKey);
  } catch (e) {
    console.error('[Maps] Loader error', e);
  }
  await bootstrapApplication(AppComponent, appConfig).catch(err => console.error(err));
})();
